/** Great-circle distance between two points, in kilometres. */

const EARTH_RADIUS_KM = 6371

const toRad = (deg: number) => (deg * Math.PI) / 180

/**
 * Haversine. Accurate to a fraction of a percent at city scale, which is all a
 * "nearest first" ordering needs — we compare distances, we never show them.
 *
 * A missing coordinate returns Infinity rather than falling back to 0,0. That
 * point is in the Gulf of Guinea, so a shop with no coordinates would read as
 * 300km from Accra and sort above a shop on the reader's own street. Infinity
 * sends it to the end of the list, which is the honest place for "we don't
 * know where this is".
 */
export function distanceKm(
  from: { lat: number; lng: number },
  to: { lat?: number | null; lng?: number | null },
): number {
  if (to.lat == null || to.lng == null || !Number.isFinite(to.lat) || !Number.isFinite(to.lng)) {
    return Number.POSITIVE_INFINITY
  }
  const dLat = toRad(to.lat - from.lat)
  const dLng = toRad(to.lng - from.lng)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(from.lat)) * Math.cos(toRad(to.lat)) * Math.sin(dLng / 2) ** 2
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(a)))
}

/**
 * Mean of the points we have — the centre of a city or country's shops.
 *
 * Used as the origin for "Near me" when the device won't give us a position:
 * someone who filtered to Miami is telling us where they're looking even
 * without location permission. Returns null when nothing has coordinates.
 */
export function centroid(
  points: Array<{ lat?: number | null; lng?: number | null }>,
): { lat: number; lng: number } | null {
  let lat = 0
  let lng = 0
  let n = 0
  for (const p of points) {
    if (p.lat == null || p.lng == null || !Number.isFinite(p.lat) || !Number.isFinite(p.lng)) continue
    lat += p.lat
    lng += p.lng
    n++
  }
  return n === 0 ? null : { lat: lat / n, lng: lng / n }
}
